import axios from "axios";
import {$apiV2, VIDEO_SERVICE_URL} from "../config/api";
import { buildSeasonSelectionQueryValue } from "../pages/tournametns/shared/utils/seasonSelection";

export function groupByNestedProperty(array, propertyPath) {
  return array.reduce((acc, obj) => {
    const key = propertyPath.split('.').reduce((o, p) => o && o[p], obj);
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(obj);
    return acc;
  }, {});
}

export const getUniqueArrayObjectsByKey = (array, key) => {
  return Array.from(
    array.reduce((map, current) => {
      if (!map.has(current[key])) {
        map.set(current[key], current);
      }
      return map;
    }, new Map()).values()
  )
}

export const findMaxSecondMarker = (data) => {
  if (!data) return null;
  let maxSecond = 5400;

  data.forEach(item => {
    const second = parseFloat(item.second);
    if (second > maxSecond) {
      maxSecond = second;
    }
  });

  return maxSecond;
}

export const getTeamParamMarkers = async (
  team_id,
  param_id,
  type = 'in_attack',
  season = '',
  controller,
  page = 1,
  competition,
  prevResponse = {},
) => {
  try {
    const seasonQueryValue = buildSeasonSelectionQueryValue(season);
    let data = prevResponse;
    const response = await $apiV2.get(
      `/platform/team/${team_id}/overview/${type}?page=${page}&param_team_id=${param_id}${seasonQueryValue ? `&year_season_id=${seasonQueryValue}` : ''}${competition ? `&season_id=${competition}` : ''}`,
      { signal: controller?.signal }
    );
    data = {
      ...response.data,
      results: data?.results?.length ? [...data.results, ...response.data?.results] : response.data?.results,
    };
    if (response.data?.next) {
      return getTeamParamMarkers(team_id, param_id, type, season, controller, page + 1, competition, data);
    }
    return Promise.resolve(data);
  } catch (e) {
    if (axios.isCancel(e)) {
      return Promise.reject({ type: 'canceled', message: `Requset was canceled` });
    }
    return Promise.reject({ type: 'error', message: e.response || e.message });
  }
};

export const getTeamAttackVideos = async (
  team_id,
  param_id,
  season = '',
  page = 1,
  prevResponse = {},
) => {
  try {
    const seasonQueryValue = buildSeasonSelectionQueryValue(season);
    let data = prevResponse;
    const response = await $apiV2.get(`/platform/team/${team_id}/overview/in_attack?page=${page}&param_team_id=${param_id}${seasonQueryValue ? `&year_season_id=${seasonQueryValue}` : ""}`);
    data = {
      ...response.data,
      results: data?.results?.length ? [...data.results, ...response.data?.results] : response.data?.results,
    };
    if (response.data?.next) {
      return getTeamAttackVideos(team_id, param_id, season, page + 1, data);
    }
    return Promise.resolve(data);
  } catch (e) {
    return Promise.reject({ type: 'error', message: e.response || e.message });
  }
};

export const getTeamDefenseVideos = async (
  team_id,
  param_id,
  season = '',
  page = 1,
  prevResponse = {},
) => {
  try {
    const seasonQueryValue = buildSeasonSelectionQueryValue(season);
    let data = prevResponse;
    const response = await $apiV2.get(`/platform/team/${team_id}/overview/in_defence?page=${page}&param_team_id=${param_id}${seasonQueryValue ? `&year_season_id=${seasonQueryValue}` : ""}`);
    data = {
      ...response.data,
      results: data?.results?.length ? [...data.results, ...response.data?.results] : response.data?.results,
    };
    if (response.data?.next) {
      return getTeamDefenseVideos(team_id, param_id, season, page + 1, data);
    }
    return Promise.resolve(data);
  } catch (e) {
    return Promise.reject({ type: 'error', message: e.response || e.message });
  }
};

export const getPlayerParamMarkers = async (
  player_id,
  param_id,
  season = '',
  controller,
  page = 1,
  competition = '',
  prevResponse = {}
) => {
  try {
    const seasonQueryValue = buildSeasonSelectionQueryValue(season);
    let data = prevResponse;
    const response = await $apiV2.get(
      `/platform/player/${player_id}/markers?page=${page}&param_id=${param_id}${seasonQueryValue ? `&year_season_id=${seasonQueryValue}` : ''}${competition ? `&season_id=${competition}` : ''}`,
      { signal: controller?.signal }
    );
    data = {
      ...response.data,
      results: data?.results?.length ? [...data.results, ...response.data?.results] : response.data?.results,
    };
    if (response.data?.next) {
      return getPlayerParamMarkers(player_id, param_id, season, controller, page + 1, competition, data);
    }
    return Promise.resolve(data);
  } catch (e) {
    if (axios.isCancel(e)) {
      return Promise.reject({ type: 'canceled', message: `Requset was canceled` });
    }
    return Promise.reject({ type: 'error', message: e.response || e.message });
  }
};


export const getGroupedAndSortedParametrs = (parametrs, withoutGroup = false) => {
  try {
    // Sort a copy so we never mutate the input array (it may be a Redux/props reference).
    const groupedParams = [...(parametrs || [])].sort((a, b) => {
      if (a.group === null) {
        return 1;
      }
      if (b.group === null) {
        return -1;
      }
      return a.group.id < b.group.id ? -1 : 1;
    })
      .reduce((group, item) => {
        if (item.group) {
          if (!group[item.group.name]) {
            group[item.group.name] = [];
          }
          group[item.group.name].push({ ...item, checked: true });
          return group;
        }

        // uncomment if you need parameters without a group
        if (withoutGroup) {
          if (!group['Without group']) {
            group['Without group'] = []
          }
          group['Without group'].push({ ...item, checked: true });
        }

        return group;
      }, {});

    // Sort each group once, after all items are collected (instead of re-sorting on every push).
    return Object.entries(groupedParams).map(([groupName, items]) => {
      const sortedItems = groupName === 'Without group'
        ? items.sort((a, b) => (a.name < b.name ? -1 : 1))
        : items.sort((a, b) => (a.sort_order < b.sort_order ? -1 : 1));
      return [groupName, sortedItems];
    });
  } catch {
    return [];
  }
}

export function extractFirstCharacters(str) {
  if (!str) return ''
    if (str == 'xG' || str == 'xA' || str == 'NPxG') {
        return str;
    }
  const tmp = str.replace('-', ' - ')
  const words = tmp.split(' ');

  const firstCharacters = words.map((word) => word.charAt(0));

  const result = firstCharacters.join('');

  return result;
}


export function mirrorCoordinates(marker) {
  try {
    let result = { ...marker }
    if (+marker?.coord_x >= 52.5) {
      const coord_x = Math.abs(105 - +marker.coord_x)
      const coord_y = Math.abs(68 - +marker.coord_y)
      result = {
        ...result,
        coord_x,
        coord_y
      }
    }
    if (+marker?.coord_x_destination >= 52.5) {
      const coord_x_destination = Math.abs(105 - +marker.coord_x_destination)
      const coord_y_destination = Math.abs(68 - +marker.coord_y_destination)
      result = {
        ...result,
        coord_x_destination,
        coord_y_destination
      }
    }
    return result;
  } catch (e) {
    console.log('error mirror coordinates marker', e);
    return marker
  }
}

export function mirrorCoordinatesMarkers(markers) {
  try {
    const new_markers = markers.map(marker => {
      const cond = +marker?.coord_x >= 52.5
      if (cond) {
        return (mirrorCoordinates(marker))
      } else {
        return marker
      }
    })

    return new_markers;
  } catch (e) {
    console.log('mirror coordinates err', e)
    return markers;
  }
}

export const combineCuts = async (params, config = {}) => {
  const result = await axios.post(
    `${VIDEO_SERVICE_URL}/batch/cut/video`,
    params,
    config,
  );

  return result.data
}

export const zipCuts = async (params, config = {}) => {
  const result = await axios.post(
    `${VIDEO_SERVICE_URL}/batch/cut/zip`,
    params,
    config,
  );

  return result.data
}


export function _calculateAge(birthday = Date.now()) { // birthday is a date
    var ageDifMs = Date.now() - birthday.getTime();
    var ageDate = new Date(ageDifMs); // miliseconds from epoch
    return Math.abs(ageDate.getUTCFullYear() - 1970);
}

export function filterObjectFromEmptyValues(obj) {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => 
      value !== null && value !== undefined && value !== ''
    )
  );
}

// We should avoid direct access to api-v0.smrtstats.com 
// for media links, this function makes sure that if we get link 
// that points to api-v0.smrtstats.com we will convert it
// to football.smrtstats.com 
export function getMediaUrl(url) {
  if (url === undefined || url === null) {
    return url
  }

  const currentDomain = window.location.hostname;
  const isProductionDomain = currentDomain === 'football.smrtstats.com';

  let prefix = ""
  if (!isProductionDomain) {
    prefix = "https://football.smrtstats.com/"
  }

  const urlParse = URL.parse(url);

  // Laravel `storage/` assets (e.g. newer team crests) are served by the API
  // host directly and are NOT proxied under the FE origin's /storage path —
  // stripping the host makes them fall through to the SPA and return index.html
  // (broken image). The API already returns a correct absolute URL, so pass
  // these through unchanged.
  if (urlParse && urlParse.pathname.startsWith('/storage/')) {
    return url;
  }

  let parsedUrl = `${prefix}${url}`
  // full url passed
  if (urlParse) {
    console.log(`Replaced "${urlParse.hostname}:${urlParse.port}" with "${prefix}"`)
    parsedUrl = `${prefix}${urlParse.pathname}`;
  } 

  if (parsedUrl.startsWith('//')) {
    parsedUrl = parsedUrl.substring(1)
  }

  console.log('getMediaUrl', { parsedUrl})

  return parsedUrl
}
